/**
 * Google Photos Picker Integration
 * Handles Google Photos selection using the Google Photos Picker API
 * https://developers.google.com/photos/picker/guides/get-started-picker
 */

import { getAccessToken } from '../utils/google-picker-loader.js';

/**
 * Open Google Photos Picker using the new Picker API
 * Uses the correct API endpoint: https://photospicker.googleapis.com/v1/sessions
 * @param {Object} component - The MediaSourcePicker component instance
 * @param {string} provider - Provider name ('google-photos')
 * @param {string} accountIdentifier - Account identifier
 */
export async function openGooglePhotosPicker(component, provider, accountIdentifier) {
  try {
    const accountInfo = component.pendingPickerAccount;

    // Step 1: Get access token from backend
    const accessToken = await getAccessToken(provider, accountIdentifier);
    if (!accessToken) {
      throw new Error('No access token received from server');
    }

    window.logger?.info('Creating Google Photos picker session', {
      provider,
      accountIdentifier,
      hasAccessToken: !!accessToken
    });

    // Step 2: Create a picker session directly with Google Photos Picker API
    const apiUrl = 'https://photospicker.googleapis.com/v1/sessions';
    const sessionResponse = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });

    if (!sessionResponse.ok) {
      const errorText = await sessionResponse.text();
      window.logger?.error('Failed to create Google Photos picker session', {
        status: sessionResponse.status,
        statusText: sessionResponse.statusText,
        error: errorText
      });
      throw new Error(`Failed to create picker session: ${sessionResponse.status} ${sessionResponse.statusText}`);
    }

    const sessionData = await sessionResponse.json();

    // The API returns 'id' not 'sessionId'
    const sessionId = sessionData.id;
    const pickerUri = sessionData.pickerUri;

    if (!sessionId) {
      window.logger?.error('Session ID is missing from response', { sessionData });
      throw new Error('Session ID not found in picker session response');
    }

    if (!pickerUri) {
      window.logger?.error('Picker URI is missing from response', { sessionData });
      throw new Error('Picker URI not found in picker session response');
    }

    window.logger?.debug('Google Photos picker session created', {
      sessionId,
      pickerUri
    });

    // Step 3: Open the picker URI (append /autoclose for web)
    const pickerUrl = `${pickerUri}/autoclose`;
    const pickerWindow = window.open(pickerUrl, 'Google Photos Picker', 'width=800,height=600');

    if (!pickerWindow) {
      throw new Error('Failed to open picker window. Please allow popups for this site.');
    }

    // Step 4: Poll the session until mediaItemsSet is true
    // Capture sessionId in a const to ensure it's available in closure
    const capturedSessionId = sessionId;
    const pollSession = async () => {
      try {
        if (!capturedSessionId) {
          window.logger?.error('Session ID is undefined when polling', { sessionId, capturedSessionId });
          throw new Error('Session ID is not available');
        }

        const statusResponse = await fetch(
          `https://photospicker.googleapis.com/v1/sessions/${capturedSessionId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          }
        );

        if (!statusResponse.ok) {
          const errorText = await statusResponse.text();
          window.logger?.error('Failed to poll Google Photos picker session', {
            status: statusResponse.status,
            error: errorText
          });
          throw new Error(`Failed to poll session status: ${statusResponse.status}`);
        }

        const statusData = await statusResponse.json();

        // Parse polling interval from response (format: "5s" -> 5000ms)
        const parsePollInterval = (intervalStr) => {
          if (!intervalStr) return 1000; // Default to 1 second
          const match = intervalStr.match(/(\d+)([sm])/);
          if (match) {
            const value = parseInt(match[1], 10);
            const unit = match[2];
            return unit === 's' ? value * 1000 : value; // Convert seconds to milliseconds
          }
          return 1000; // Default fallback
        };

        if (statusData.mediaItemsSet) {
          // Step 5: Media items are selected, fetch them directly from Google Photos Picker API
          window.logger?.debug('Media items selected, fetching list', { sessionId: capturedSessionId });

          // Try the mediaItems endpoint - if this returns 404, the endpoint path might be wrong
          const mediaItemsUrl = `https://photospicker.googleapis.com/v1/mediaItems?sessionId=${capturedSessionId}`;
          window.logger?.debug('Fetching media items from', { url: mediaItemsUrl });

          const itemsResponse = await fetch(mediaItemsUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          });

          if (!itemsResponse.ok) {
            const errorText = await itemsResponse.text();
            window.logger?.error('Failed to fetch Google Photos picker media items', {
              status: itemsResponse.status,
              statusText: itemsResponse.statusText,
              error: errorText,
              url: mediaItemsUrl
            });
            throw new Error(`Failed to fetch selected media items: ${itemsResponse.status} ${itemsResponse.statusText}`);
          }

          const itemsData = await itemsResponse.json();
          window.logger?.debug('Media items response', { itemsData });

          // Step 6: Transform media items to our internal format
          const files = (itemsData.mediaItems || []).map(item => ({
            provider: 'google-photos',
            accountId: accountIdentifier,
            fileId: item.id,
            fileUrl: `oauth://google-photos/${accountIdentifier}/${item.id}`,
            fileName: item.filename || item.description || 'Photo',
            mimeType: item.mimeType || 'image/jpeg',
            thumbnailUrl: item.baseUrl ? `${item.baseUrl}=w200-h200` : null,
            originalImageUrl: item.baseUrl || null
          }));

          // Step 7: Handle selected files
          component.currentAccount = accountInfo;
          try {
            handlePhotoPickerCallback(component, { files });
          } finally {
            component.currentAccount = null;
            component.pendingPickerAccount = null;
          }

          // Close picker window if still open
          if (pickerWindow && !pickerWindow.closed) {
            pickerWindow.close();
          }

          // Reset guard
          component.isOpeningPicker = false;
        } else {
          // Continue polling using the interval from pollingConfig
          const pollInterval = statusData.pollingConfig?.pollInterval
            ? parsePollInterval(statusData.pollingConfig.pollInterval)
            : 1000; // Default to 1 second
          setTimeout(pollSession, pollInterval);
        }
      } catch (error) {
        window.logger?.error('Error polling Google Photos picker session:', error);
        component.showError(error.message || 'Failed to check picker status. Please try again.');
        if (pickerWindow && !pickerWindow.closed) {
          pickerWindow.close();
        }
        component.isOpeningPicker = false;
      }
    };

    // Start polling after a short delay
    setTimeout(pollSession, 1000);

    // Also check if window was closed manually
    const checkWindowClosed = setInterval(() => {
      if (pickerWindow.closed) {
        clearInterval(checkWindowClosed);
        window.logger?.debug('Picker window closed by user');
        component.isOpeningPicker = false;
        component.pendingPickerAccount = null;
      }
    }, 500);

    // Reset guard after picker window is opened
    // Note: isOpeningPicker will be reset when polling completes or window closes
  } catch (error) {
    window.logger?.error('Error opening Google Photos picker: ' + error);
    component.showError(error.message || 'Failed to open Google Photos picker. Please try again.');
    // Reset guard on error
    component.isOpeningPicker = false;
  }
}

/**
 * Handle Google Photos Picker callback
 * @param {Object} component - The MediaSourcePicker component instance
 * @param {Object} data - Data from picker, should have a 'files' array
 */
export function handlePhotoPickerCallback(component, data) {
  if (data.files && Array.isArray(data.files) && data.files.length > 0) {
    // Files are already in the correct format from the backend
    const files = data.files.map(file => ({
      provider: file.provider || component.currentAccount?.provider,
      accountId: file.accountId || component.currentAccount?.accountIdentifier,
      fileId: file.fileId,
      fileUrl: file.fileUrl,
      fileName: file.fileName || 'Photo',
      mimeType: file.mimeType,
      thumbnailUrl: file.thumbnailUrl,
      originalImageUrl: file.originalImageUrl
    }));

    // Dispatch files-selected event with textarea reference
    component.dispatchEvent(new CustomEvent('files-selected', {
      detail: {
        files,
        textarea: component._targetTextarea || null
      },
      bubbles: true
    }));

    // Close picker modal
    component.close();
  } else {
    window.logger?.warn('Google Photos picker callback received no files', data);
  }
}

